import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation, useQuery, type ApolloCache, type ApolloClient } from "@apollo/client";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import { useAssumeUser } from "@/providers/AssumeUserProvider/AssumeUserProvider";
import {
  IMPORT_RECENT_VISITS_MUTATION,
  RECORD_RECENT_VISIT_MUTATION,
  REMOVE_RECENT_VISIT_MUTATION,
  type ImportRecentVisitInput,
  type RecentVisitsMutationPayload
} from "./recentlyVisited/mutations";
import {
  RECENTLY_VISITED_QUERY,
  RECENTLY_VISITED_QUERY_FIRST,
  type RecentVisitEntityTypeGql,
  type RecentlyVisitedQueryVisit
} from "./recentlyVisited/queries";

export const RECENTLY_VISITED_PER_TYPE_MAX = 50;
export const RECENTLY_VISITED_PERSIST_CAP = 200;
export const LEGACY_RECENTLY_VISITED_STORAGE_KEY = "recentlyVisited";

export type RecentType = "people" | "jobs" | "facilities" | "sections" | "events" | "activities" | "other";

export type RecentlyVisitedItem = {
  url: string;
  text: string;
  subtext: string;
  image: string;
  dateTime: string;
  type: RecentType;
  entityId: string;
};

export type UseRecentlyVisitedOptions = {
  /** Skip the persist-cap list query. Entity pages that only record a visit should pass true. */
  skipListQuery?: boolean;
};

export type GetRecentlyVisitedOptions = {
  /** Per-type display cap. `null` returns every stored item (for ranking / GraphQL ids). */
  capPerType?: number | null;
};

const ENTITY_TYPE_TO_GQL: Record<RecentType, RecentVisitEntityTypeGql> = {
  people: "PEOPLE",
  jobs: "JOBS",
  facilities: "FACILITIES",
  sections: "SECTIONS",
  events: "EVENTS",
  activities: "ACTIVITIES",
  other: "OTHER"
};

const importStartedSubs = new Set<string>();
const importPromises = new Map<string, Promise<void>>();
const listQueryHydratedClients = new Set<ApolloClient<object>>();

export function recentlyVisitedImportedAtStorageKey(authenticatedUserSub: string): string {
  return `m360.recentlyVisited.importedAt:${authenticatedUserSub}`;
}

export function clearRecentlyVisitedImportLocksForTests(): void {
  importStartedSubs.clear();
  importPromises.clear();
  listQueryHydratedClients.clear();
}

export function inferTypeByUrl(url: string | undefined | null): RecentType {
  if (!url) return "other";
  if (url.startsWith("/people/")) return "people";
  if (url.startsWith("/jobs/")) return "jobs";
  if (url.startsWith("/facilities/")) return "facilities";
  if (url.startsWith("/sections/")) return "sections";
  if (url.startsWith("/events/") || url.startsWith("/activities/events/")) return "events";
  if (url.startsWith("/activities/")) return "activities";
  return "other";
}

export function matchesRecentFilter(itemType: RecentType, filter: "all" | RecentType): boolean {
  if (filter === "all") return true;
  if (filter === "activities") return itemType === "activities" || itemType === "events";
  return itemType === filter;
}

const JOB_POST_ID_PATTERN = /\/jobs\/job-post\/([^/]+)/;
const ACTIVITY_INSTANCE_ID_PATTERN = /\/activities\/activity-types\/[^/]+\/activity\/([^/]+)/;
const ACTIVITIES_EVENT_SLUG_PATTERN = /\/activities\/events\/([^/]+)/;
const LEGACY_EVENT_SLUG_PATTERN = /^\/events\/([^/]+)/;
const SKIPPED_ENTITY_IDS = new Set(["create", "new", "pat-results"]);

export type RecentlyViewedEntityType = "people" | "facilities" | "jobs" | "activities";

export function entityIdFromUrl(url: string | undefined | null, type?: RecentType): string {
  if (!url) return "";
  const itemType = type ?? inferTypeByUrl(url);

  if (itemType === "people") return url.slice("/people/".length).split("/")[0] ?? "";
  if (itemType === "facilities") return url.slice("/facilities/".length).split("/")[0] ?? "";
  if (itemType === "jobs") return url.match(JOB_POST_ID_PATTERN)?.[1] ?? "";
  if (itemType === "sections") return url.slice("/sections/".length).split("/")[0] ?? "";
  if (itemType === "events") {
    return url.match(ACTIVITIES_EVENT_SLUG_PATTERN)?.[1] ?? url.match(LEGACY_EVENT_SLUG_PATTERN)?.[1] ?? "";
  }
  if (itemType === "activities") return url.match(ACTIVITY_INSTANCE_ID_PATTERN)?.[1] ?? "";
  return url;
}

function isRecentType(value: string): value is RecentType {
  return value in ENTITY_TYPE_TO_GQL;
}

function isRecentVisitEntityTypeGql(value: string): value is RecentVisitEntityTypeGql {
  return (Object.values(ENTITY_TYPE_TO_GQL) as string[]).includes(value);
}

function coerceRecentType(value: string | null | undefined, url: string): RecentType {
  if (value && isRecentType(value)) return value;
  if (value && isRecentVisitEntityTypeGql(value)) {
    const mapped = value.toLowerCase();
    if (isRecentType(mapped)) return mapped;
  }
  return inferTypeByUrl(url);
}

export function recentVisitIdentity(visit: {
  url?: string | null;
  entityType?: string | null;
  type?: string | null;
  entityId?: string | null;
}): string {
  const url = visit.url?.trim() ?? "";
  const type = coerceRecentType(visit.entityType ?? visit.type, url);
  const id = visit.entityId?.trim() || entityIdFromUrl(url, type) || url;
  if (!type || !id) return "";
  return `${type}:${id}`;
}

function entityIdFromRecent(item: RecentlyVisitedItem, type: RecentlyViewedEntityType): string | undefined {
  const itemType = (item.type as RecentType | undefined) || inferTypeByUrl(item.url);
  if (type === "people" || type === "facilities" || type === "jobs") {
    if (itemType !== type) return undefined;
  } else if (itemType !== "activities" && itemType !== "events") {
    return undefined;
  }

  const id = item.entityId?.trim() || entityIdFromUrl(item.url, itemType);
  return id || undefined;
}

export function recentlyViewedEntityIds(
  items: readonly RecentlyVisitedItem[],
  type: RecentlyViewedEntityType
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    const id = entityIdFromRecent(item, type);
    if (!id || SKIPPED_ENTITY_IDS.has(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }

  return ids;
}

function emptyGroups(): Record<RecentType, RecentlyVisitedItem[]> {
  return {
    people: [],
    jobs: [],
    facilities: [],
    sections: [],
    events: [],
    activities: [],
    other: []
  };
}

function sortDesc(a: RecentlyVisitedItem, b: RecentlyVisitedItem) {
  return Date.parse(b.dateTime) - Date.parse(a.dateTime);
}

function takeGroup(items: RecentlyVisitedItem[], cap: number | null): RecentlyVisitedItem[] {
  return cap == null ? items : items.slice(0, cap);
}

function entityTypeFromGraphql(entityType: RecentVisitEntityTypeGql | undefined, url: string): RecentType {
  if (!entityType) return inferTypeByUrl(url);
  const mapped = entityType.toLowerCase() as RecentType;
  return mapped in ENTITY_TYPE_TO_GQL ? mapped : inferTypeByUrl(url);
}

function visitToItem(visit: RecentlyVisitedQueryVisit): RecentlyVisitedItem {
  const type = entityTypeFromGraphql(visit.entityType, visit.url);
  return {
    url: visit.url,
    text: visit.text,
    subtext: visit.subtext,
    image: visit.image,
    dateTime: visit.visitedAt,
    type,
    entityId: visit.entityId || entityIdFromUrl(visit.url, type)
  };
}

function normalizeByType(
  items: RecentlyVisitedItem[],
  capForType: (type: RecentType) => number | null
): RecentlyVisitedItem[] {
  const groups = emptyGroups();

  for (const item of items) {
    const type = (item.type as RecentType) || inferTypeByUrl(item.url);
    groups[type].push({ ...item, type });
  }

  (Object.keys(groups) as RecentType[]).forEach((type) => {
    groups[type].sort(sortDesc);
    const seen = new Set<string>();
    groups[type] = groups[type].filter((item) => {
      const identity = recentVisitIdentity(item);
      if (!identity || seen.has(identity)) return false;
      seen.add(identity);
      return true;
    });
  });

  const merged = [
    ...takeGroup(groups.people, capForType("people")),
    ...takeGroup(groups.jobs, capForType("jobs")),
    ...takeGroup(groups.facilities, capForType("facilities")),
    ...takeGroup(groups.sections, capForType("sections")),
    ...takeGroup(groups.events, capForType("events")),
    ...takeGroup(groups.activities, capForType("activities")),
    ...takeGroup(groups.other, capForType("other"))
  ];

  return merged.sort(sortDesc);
}

function readLegacyRecentlyVisitedFromStorage(): RecentlyVisitedItem[] {
  const raw = localStorage.getItem(LEGACY_RECENTLY_VISITED_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentlyVisitedItem[]) : [];
  } catch {
    return [];
  }
}

function visitedAtIso(item: RecentlyVisitedItem): string {
  const parsed = Date.parse(item.dateTime);
  return Number.isNaN(parsed) ? new Date().toISOString() : new Date(parsed).toISOString();
}

function toImportVisit(item: RecentlyVisitedItem): ImportRecentVisitInput | null {
  if (!item.url || !item.text) return null;
  const url = item.url.trim();
  if (!url) return null;
  const type = item.type ?? inferTypeByUrl(url);
  const entityId = item.entityId?.trim() || entityIdFromUrl(url, type) || url;
  return {
    url,
    text: item.text,
    ...(item.subtext ? { subtext: item.subtext } : {}),
    ...(item.image ? { image: item.image } : {}),
    visitedAt: visitedAtIso(item),
    entityType: ENTITY_TYPE_TO_GQL[type],
    entityId
  };
}

export function capRecentlyVisitedImportVisits(visits: ImportRecentVisitInput[]): ImportRecentVisitInput[] {
  const newestByEntity = new Map<string, ImportRecentVisitInput>();
  for (const visit of visits) {
    const identity = recentVisitIdentity(visit);
    if (!identity) continue;
    const current = newestByEntity.get(identity);
    if (!current || Date.parse(visit.visitedAt) >= Date.parse(current.visitedAt)) {
      newestByEntity.set(identity, visit);
    }
  }

  const groups = new Map<string, ImportRecentVisitInput[]>();
  for (const visit of newestByEntity.values()) {
    const type = visit.entityType ?? ENTITY_TYPE_TO_GQL[inferTypeByUrl(visit.url)];
    const list = groups.get(type) ?? [];
    list.push(visit);
    groups.set(type, list);
  }

  const capped: ImportRecentVisitInput[] = [];
  for (const list of groups.values()) {
    capped.push(
      ...[...list]
        .sort((left, right) => Date.parse(right.visitedAt) - Date.parse(left.visitedAt))
        .slice(0, RECENTLY_VISITED_PERSIST_CAP)
    );
  }
  return capped;
}

function writeRecentVisitsCache(cache: ApolloCache<unknown>, recentVisits: RecentlyVisitedQueryVisit[]): void {
  cache.writeQuery({
    query: RECENTLY_VISITED_QUERY,
    variables: { first: RECENTLY_VISITED_QUERY_FIRST },
    data: { recentlyVisited: recentVisits }
  });
}

function readRecentVisitsCache(cache: ApolloCache<unknown>): RecentlyVisitedQueryVisit[] {
  return (
    cache.readQuery<{ recentlyVisited: RecentlyVisitedQueryVisit[] }>({
      query: RECENTLY_VISITED_QUERY,
      variables: { first: RECENTLY_VISITED_QUERY_FIRST }
    })?.recentlyVisited ?? []
  );
}

function writeVisitsIfSuccessful(cache: ApolloCache<unknown>, payload: RecentVisitsMutationPayload | undefined): void {
  if (!payload?.success) return;
  writeRecentVisitsCache(cache, payload.recentVisits);
}

function mergeRecordedVisit(cache: ApolloCache<unknown>, payload: RecentVisitsMutationPayload | undefined): void {
  if (!payload?.success) return;
  const recorded = payload.recentVisits[0];
  if (!recorded) return;
  const recordedKey = `${recorded.entityType}:${recorded.entityId}`;
  writeRecentVisitsCache(cache, [
    recorded,
    ...readRecentVisitsCache(cache).filter((visit) => `${visit.entityType}:${visit.entityId}` !== recordedKey)
  ]);
}

export function removeVisitFromCache(
  cache: ApolloCache<unknown>,
  visit: { entityType: RecentVisitEntityTypeGql; entityId: string }
): void {
  const removedKey = `${visit.entityType}:${visit.entityId}`;
  writeRecentVisitsCache(
    cache,
    readRecentVisitsCache(cache).filter((cached) => `${cached.entityType}:${cached.entityId}` !== removedKey)
  );
}

export function useRecentlyVisited(options: UseRecentlyVisitedOptions = {}) {
  const skipListQuery = options.skipListQuery === true;
  const { user } = useCurrentUser();
  const authenticatedUserSub = user.sub;
  const { assumedUser } = useAssumeUser();
  const { isCompactLayout } = useIsCompactLayout();
  const client = useApolloClient();
  const {
    data,
    loading: queryLoading,
    error: recentlyVisitedError
  } = useQuery(RECENTLY_VISITED_QUERY, {
    variables: { first: RECENTLY_VISITED_QUERY_FIRST },
    fetchPolicy: listQueryHydratedClients.has(client) ? "cache-first" : "cache-and-network",
    nextFetchPolicy: "cache-first",
    skip: skipListQuery,
    onCompleted() {
      listQueryHydratedClients.add(client);
    }
  });
  const visitCount = data?.recentlyVisited?.length ?? 0;
  const importMarkerKey = authenticatedUserSub ? recentlyVisitedImportedAtStorageKey(authenticatedUserSub) : "";
  const awaitingLegacyImport =
    !skipListQuery && Boolean(authenticatedUserSub) && !assumedUser && !localStorage.getItem(importMarkerKey);
  const [legacyImportSettled, setLegacyImportSettled] = useState(false);
  const loading =
    !skipListQuery &&
    visitCount === 0 &&
    !recentlyVisitedError &&
    (queryLoading || data == null || (awaitingLegacyImport && !legacyImportSettled));
  const [recordRecentVisit] = useMutation(RECORD_RECENT_VISIT_MUTATION, {
    ignoreResults: true,
    update(cache, { data: mutationData }) {
      mergeRecordedVisit(cache, mutationData?.recordRecentVisit);
    },
    onError() {
      showErrorSnackbar("Couldn't save this page to Recently Visited.", { subtitle: "Try again." });
    },
    onCompleted(mutationData) {
      if (mutationData.recordRecentVisit?.success === false) {
        showErrorSnackbar("Couldn't save this page to Recently Visited.", { subtitle: "Try again." });
      }
    }
  });
  const [removeRecentVisit] = useMutation(REMOVE_RECENT_VISIT_MUTATION, {
    ignoreResults: true,
    update(cache, { data: mutationData }, { variables }) {
      if (!mutationData?.removeRecentVisit?.success) return;
      if (!variables?.entityType || !variables.entityId) return;
      removeVisitFromCache(cache, { entityType: variables.entityType, entityId: variables.entityId });
    },
    onError() {
      showErrorSnackbar("Couldn't remove that item from Recently Visited.", { subtitle: "Try again." });
    },
    onCompleted(mutationData) {
      if (mutationData.removeRecentVisit?.success === false) {
        showErrorSnackbar("Couldn't remove that item from Recently Visited.", { subtitle: "Try again." });
      }
    }
  });
  const [importRecentVisits] = useMutation(IMPORT_RECENT_VISITS_MUTATION, {
    ignoreResults: true,
    update(cache, { data: mutationData }) {
      writeVisitsIfSuccessful(cache, mutationData?.importRecentVisits);
    },
    onError() {
      showErrorSnackbar("Couldn't restore Recently Visited from this browser.", {
        subtitle: "Try refreshing the page."
      });
    },
    onCompleted(mutationData) {
      if (mutationData.importRecentVisits?.success === false) {
        showErrorSnackbar("Couldn't restore Recently Visited from this browser.", {
          subtitle: "Try refreshing the page."
        });
      }
    }
  });

  const getRecentlyVisitedItems = useCallback(
    (options?: GetRecentlyVisitedOptions): RecentlyVisitedItem[] => {
      const capPerType = options && "capPerType" in options ? options.capPerType : RECENTLY_VISITED_PER_TYPE_MAX;
      return normalizeByType((data?.recentlyVisited ?? []).map(visitToItem), () => capPerType ?? null);
    },
    [data?.recentlyVisited]
  );

  const recordRecentVisitRef = useRef(recordRecentVisit);
  recordRecentVisitRef.current = recordRecentVisit;
  const removeRecentVisitRef = useRef(removeRecentVisit);
  removeRecentVisitRef.current = removeRecentVisit;

  const addRecentlyVisitedItem = useCallback(
    (item: Omit<RecentlyVisitedItem, "dateTime">) => {
      const url = item.url?.trim();
      const entityId = item.entityId?.trim();
      if (!url || !entityId || !item.type) return;
      void recordRecentVisitRef.current({
        variables: {
          input: {
            url,
            text: item.text,
            ...(item.subtext ? { subtext: item.subtext } : {}),
            ...(item.image ? { image: item.image } : {}),
            entityType: ENTITY_TYPE_TO_GQL[item.type],
            entityId,
            client: isCompactLayout ? "M360_MOBILE_WEB" : "M360_WEB"
          }
        }
      });
    },
    [isCompactLayout]
  );

  const removeRecentlyVisitedItem = useCallback((item: Pick<RecentlyVisitedItem, "type" | "entityId">) => {
    const entityId = item.entityId?.trim();
    if (!item.type || !entityId) return;
    void removeRecentVisitRef.current({
      variables: {
        entityType: ENTITY_TYPE_TO_GQL[item.type],
        entityId
      }
    });
  }, []);

  useEffect(() => {
    if (skipListQuery) return;
    if (!authenticatedUserSub || assumedUser) return;
    if (queryLoading) return;
    if (localStorage.getItem(importMarkerKey)) return;

    let cancelled = false;
    const settleIfMounted = () => {
      if (!cancelled) setLegacyImportSettled(true);
    };

    const pendingImport = importPromises.get(authenticatedUserSub);
    if (pendingImport) {
      void pendingImport.finally(settleIfMounted);
      return () => {
        cancelled = true;
      };
    }

    let settleImport!: () => void;
    const importRun = new Promise<void>((resolve) => {
      settleImport = resolve;
    });
    importPromises.set(authenticatedUserSub, importRun);
    importStartedSubs.add(authenticatedUserSub);

    const visitsToImport = capRecentlyVisitedImportVisits(
      readLegacyRecentlyVisitedFromStorage()
        .map(toImportVisit)
        .filter((visit): visit is ImportRecentVisitInput => visit != null)
    );

    void importRecentVisits({ variables: { visits: visitsToImport } })
      .then((result) => {
        if (result.data?.importRecentVisits.success) {
          localStorage.setItem(importMarkerKey, new Date().toISOString());
          return;
        }
        importStartedSubs.delete(authenticatedUserSub);
      })
      .catch(() => {
        importStartedSubs.delete(authenticatedUserSub);
      })
      .finally(() => {
        importPromises.delete(authenticatedUserSub);
        settleImport();
        settleIfMounted();
      });

    return () => {
      cancelled = true;
    };
  }, [assumedUser, authenticatedUserSub, importMarkerKey, importRecentVisits, queryLoading, skipListQuery]);

  return {
    getRecentlyVisitedItems,
    addRecentlyVisitedItem,
    removeRecentlyVisitedItem,
    loading,
    error: skipListQuery ? undefined : recentlyVisitedError
  };
}
